import firebase from "firebase/app";
import "firebase/firestore";
import Firestore = firebase.firestore.Firestore;

export interface RepoIndex {
  repos: RepoRef[];
}

export interface RepoRef {
  id: string;
  title: string;
  docCount: number;
}

export interface Repo {
  title: string;
  name: string;
  url: string;
  licenses: License[];
  docs: DocRef[];
}

export interface RepoDocTree {
  title: string;
  name: string;
  url: string;
  licenses: License[];
  docs: Node<DocRef>;
}

export interface DocRef {
  id: string;
  title: string;
  indent: number;
}

export interface License {
  identifier: string;
  link: string;
  confidence: number;
}

export function newRepoIndex(): RepoIndex {
  return {
    repos: []
  };
}

export function newRepoRef(): RepoRef {
  return {
    id: "",
    title: "",
    docCount: 0
  };
}

export function newRepo(): Repo {
  return {
    title: "",
    name: "",
    url: "",
    licenses: [],
    docs: []
  };
}

export function getRepoRef(id: string, repo: Repo): RepoRef {
  return {
    id,
    title: repo.title,
    docCount: repo.docs.length
  };
}

export function newRepoDocTree(): RepoDocTree {
  return {
    title: "",
    name: "",
    url: "",
    licenses: [],
    docs: {
      val: undefined,
      children: []
    }
  };
}

export function newDocRef(): DocRef {
  return {
    id: "",
    title: "",
    indent: 0
  };
}

export function buildTree(docs: DocRef[]): Node<DocRef> {
  interface Element {
    node: Node<DocRef>;
    indent: number;
  }

  const root: Element = {
    node: {
      val: undefined,
      children: []
    },
    indent: -1
  };

  const stack = [root];

  for (const doc of docs) {
    const curr = {
      node: {
        val: doc,
        children: []
      },
      indent: doc.indent || 0
    };
    while (curr.indent <= stack[0].indent) {
      stack.shift();
    }
    const top = stack[0].node;
    top.children = [...top.children, curr.node];
    stack.unshift(curr);
  }

  return root.node;
}

export class RepoService {
  private firestore: Firestore;

  constructor() {
    this.firestore = firebase.firestore();
  }

  async getRepoIndex(): Promise<RepoIndex> {
    const repoIndexSnapshot = await this.firestore.doc(`repoIndices/0`).get();
    return repoIndexSnapshot.data() as RepoIndex;
  }

  async getRepo(id: string): Promise<Repo> {
    const repoSnapshot = await this.firestore.doc(`repos/${id}`).get();
    return repoSnapshot.data() as Repo;
  }

  async getRepoDocTree(id: string): Promise<RepoDocTree> {
    const repo = await this.getRepo(id);
    return {
      title: repo.title,
      name: repo.name,
      url: repo.url,
      licenses: repo.licenses,
      docs: buildTree(repo.docs)
    };
  }
}

export interface Node<T> {
  val: T | undefined;
  children: Node<T>[];
}

function printNodeRec<T>(node: Node<T>, indent: number) {
  let ind = "";
  for (let i = 0; i < indent * 3; i++) {
    ind += " ";
  }
  console.log(
    ind + " " + JSON.stringify(node.val) + " " + node.children.length
  );
  const newIndent = indent + 1;
  for (const child of node.children) {
    printNodeRec(child, newIndent);
  }
}

export function printNode<T>(node: Node<T>) {
  printNodeRec(node, 0);
}
